
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardFooter, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { CollaborationProject } from '@/types/collaboration';
import { formatDistanceToNow } from 'date-fns';
import { 
  Music, 
  Mic, 
  Headphones, 
  FileAudio, 
  PenTool,
  Users
} from 'lucide-react';

interface ProjectCardProps {
  project: CollaborationProject;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
  const navigate = useNavigate();
  
  const getProjectTypeIcon = () => {
    switch (project.type) {
      case 'writing':
        return <PenTool className="h-4 w-4" />;
      case 'recording':
        return <Mic className="h-4 w-4" />;
      case 'production':
        return <Music className="h-4 w-4" />;
      case 'mixing':
      case 'mastering':
        return <Headphones className="h-4 w-4" />;
      default:
        return <FileAudio className="h-4 w-4" />;
    }
  };
  
  const getStatusBadgeColor = () => {
    switch (project.status) {
      case 'active':
        return 'bg-green-500/20 text-green-500 hover:bg-green-500/20';
      case 'archived':
        return 'bg-slate-500/20 text-slate-500 hover:bg-slate-500/20';
      case 'completed':
        return 'bg-blue-500/20 text-blue-500 hover:bg-blue-500/20';
      default:
        return '';
    }
  };
  
  return (
    <Card 
      className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer animate-fade-in"
      onClick={() => navigate(`/collaboration/projects/${project.id}`)}
    >
      <div className="h-32 overflow-hidden">
        <img 
          src={project.thumbnail || 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=600'} 
          alt={project.name}
          className="w-full h-full object-cover"
        />
      </div>
      <CardHeader className="p-4 pb-2">
        <div className="flex justify-between">
          <Badge variant="outline" className="mb-2 gap-1 flex items-center">
            {getProjectTypeIcon()}
            <span className="capitalize">{project.type}</span>
          </Badge>
          <Badge variant="outline" className={getStatusBadgeColor()}>
            {project.status}
          </Badge>
        </div>
        <CardTitle className="text-md">{project.name}</CardTitle>
        <CardDescription className="line-clamp-2 text-xs">
          {project.description}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 pt-0 pb-2">
        <p className="text-xs text-muted-foreground">
          Last updated: {formatDistanceToNow(new Date(project.lastUpdated), { addSuffix: true })}
        </p>
      </CardContent>
      <CardFooter className="p-4 pt-2 flex justify-between">
        <div className="flex -space-x-2">
          {project.collaborators.slice(0, 3).map((collaborator) => (
            <Avatar key={collaborator.id} className="border-2 border-background h-7 w-7">
              <AvatarImage src={collaborator.avatarUrl} />
              <AvatarFallback className="text-xs">
                {collaborator.name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
          ))}
          {project.collaborators.length > 3 && (
            <div className="bg-muted flex items-center justify-center h-7 w-7 rounded-full border-2 border-background text-xs">
              +{project.collaborators.length - 3}
            </div>
          )}
        </div>
        <div className="flex items-center text-xs text-muted-foreground">
          <Users className="h-3 w-3 mr-1" />
          {project.collaborators.length} collaborators
        </div>
      </CardFooter>
    </Card>
  );
};

export default ProjectCard;
